/* 90MPH Club — Dawn Cart Drawer enhancer v1.0 */
(() => {
  if (window.__mphCartDrawerReady) return;
  window.__mphCartDrawerReady = true;

  const config = Object.assign({
    freeShippingThreshold: 5000,
    freeShippingText: 'Spend [amount] more to reach free shipping!',
    freeShippingSuccess: 'You have unlocked free shipping!',
    orderNoteText: 'Order note',
    shippingText: 'Shipping',
    viewCartText: 'View cart'
  }, window.MPH_CART_DRAWER || {});

  const rootPath = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const cartUrl = () => `${rootPath()}cart`;
  const moneyFormat = document.documentElement.dataset.moneyFormat || '${{amount}}';
  const formatMoney = cents => {
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, moneyFormat);
    }
    const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'INR';
    return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
      style: 'currency', currency
    }).format((Number(cents) || 0) / 100);
  };

  const getDrawer = () => document.querySelector('cart-drawer');
  let refreshPromise = null;
  let refreshTimer = null;

  const utilityMarkup = () => `
    <div class="mph-cart__utility" data-mph-cart-utility>
      <div class="mph-cart__utility-item">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><rect x="5" y="3" width="14" height="18" rx="3"/><path d="M8 8h8M8 12h8M8 16h5"/></svg>
        <span>${config.orderNoteText}</span>
      </div>
      <div class="mph-cart__utility-item">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="m4 7 8-4 8 4v10l-8 4-8-4V7Z"/><path d="m4 7 8 4 8-4M12 11v10"/></svg>
        <span>${config.shippingText}</span>
      </div>
    </div>`;

  const progressMarkup = () => `
    <div class="mph-cart__progress" data-mph-cart-progress aria-live="polite">
      <p class="mph-cart__progress-copy" data-mph-progress-copy></p>
      <div class="mph-cart__progress-track" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0">
        <span class="mph-cart__progress-fill"></span>
      </div>
    </div>`;

  const decorate = async cartData => {
    const drawer = getDrawer();
    if (!drawer) return;
    const cart = cartData || await fetch(`${rootPath()}cart.js`, { headers: { Accept: 'application/json' } }).then(r => r.json());
    const header = drawer.querySelector('.drawer__header');
    const heading = drawer.querySelector('.drawer__heading');
    if (heading) heading.innerHTML = `Cart <sup class="mph-cart__count">${cart.item_count}</sup>`;

    if (header && !drawer.querySelector('[data-mph-cart-progress]')) {
      header.insertAdjacentHTML('afterend', progressMarkup());
    }
    const progress = drawer.querySelector('[data-mph-cart-progress]');
    if (progress) {
      const remaining = Math.max(0, Number(config.freeShippingThreshold) - cart.total_price);
      const percent = config.freeShippingThreshold > 0
        ? Math.min(100, (cart.total_price / config.freeShippingThreshold) * 100)
        : 100;
      const copy = progress.querySelector('[data-mph-progress-copy]');
      copy.innerHTML = remaining > 0
        ? config.freeShippingText.replace('[amount]', `<strong>${formatMoney(remaining)}</strong>`)
        : `<strong>${config.freeShippingSuccess}</strong>`;
      const bar = progress.querySelector('.mph-cart__progress-track');
      bar.style.setProperty('--mph-cart-progress', `${percent}%`);
      bar.setAttribute('aria-valuenow', String(Math.round(percent)));
    }

    const footer = drawer.querySelector('.drawer__footer');
    if (footer && !drawer.querySelector('[data-mph-cart-utility]')) {
      footer.insertAdjacentHTML('beforebegin', utilityMarkup());
    }
    const checkout = drawer.querySelector('#CartDrawer-Checkout, .cart__checkout-button');
    if (checkout && !drawer.querySelector('.mph-cart__view-cart')) {
      checkout.insertAdjacentHTML('afterend', `<a class="mph-cart__view-cart" href="${cartUrl()}">${config.viewCartText}</a>`);
    }
  };

  const openDrawer = () => {
    const drawer = getDrawer();
    if (!drawer) return;
    if (typeof drawer.open === 'function') drawer.open();
    else {
      drawer.classList.add('animate', 'active');
      document.body.classList.add('overflow-hidden');
    }
  };

  const replaceSection = (id, html, selector) => {
    if (!html) return;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const incoming = parsed.querySelector(selector);
    const current = document.querySelector(selector);
    if (incoming && current) current.innerHTML = incoming.innerHTML;
  };

  const refreshAndOpen = async () => {
    if (refreshPromise) return refreshPromise;
    refreshPromise = (async () => {
      try {
        const [sectionsResponse, cartResponse] = await Promise.all([
          fetch(`${cartUrl()}?sections=cart-drawer,cart-icon-bubble`, { headers: { Accept: 'application/json' } }),
          fetch(`${rootPath()}cart.js`, { headers: { Accept: 'application/json' } })
        ]);
        const cart = await cartResponse.json();
        if (sectionsResponse.ok) {
          const sections = await sectionsResponse.json();
          replaceSection('cart-drawer', sections['cart-drawer'], 'cart-drawer .drawer__inner');
          replaceSection('cart-icon-bubble', sections['cart-icon-bubble'], '#cart-icon-bubble');
        }
        await decorate(cart);
        openDrawer();
        document.dispatchEvent(new CustomEvent('mph:cart-drawer:refreshed', { detail: { cart } }));
      } catch (error) {
        console.warn('[90MPH cart drawer] Refresh failed:', error);
        await decorate().catch(() => {});
        openDrawer();
      } finally {
        refreshPromise = null;
      }
    })();
    return refreshPromise;
  };

  const scheduleRefresh = () => {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(refreshAndOpen, 60);
  };

  ['cart:refresh', 'cart:updated', 'cart:update', 'cartUpdate'].forEach(name => {
    document.addEventListener(name, scheduleRefresh);
  });

  const observer = new MutationObserver(mutations => {
    const nativeInnerRefresh = mutations.some(m => {
      if (!(m.target instanceof Element) || !m.target.matches('cart-drawer .drawer__inner')) return false;
      return [...m.addedNodes].some(node => {
        if (!(node instanceof Element)) return false;
        return !node.matches('[data-mph-cart-progress], [data-mph-cart-utility]');
      });
    });
    if (!nativeInnerRefresh) return;
    const drawer = getDrawer();
    if (drawer && (drawer.classList.contains('active') || drawer.classList.contains('animate'))) {
      decorate().catch(() => {});
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });

  const boot = () => decorate().catch(() => {});
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();

  document.addEventListener('shopify:section:load', boot);
})();
